// src/components/UpdateLeaves.js
import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Modal,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import { FaMinus } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import DatePicker from "react-multi-date-picker";
import { io } from "socket.io-client";



const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const socket = io(API_URL, { transports: ["websocket"] });

const UpdateLeaves = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const prevMonthDate = new Date(currentYear, currentMonth, 1);
  const previousMonth = prevMonthDate.toISOString().slice(0, 7);

  const minMonth = `${currentYear}-01`;
  const maxMonth = previousMonth;

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [employee, setEmployee] = useState("");

  const [usedUnplannedLeave, setUsedUnplannedLeave] = useState(0);
  const [usedPlannedLeave, setUsedPlannedLeave] = useState(0);
  const [remainingUnplannedLeave, setRemainingUnplannedLeave] = useState(0);
  const [remainingPlannedLeave, setRemainingPlannedLeave] = useState(0);
  const [extraLeaves, setExtraLeaves] = useState(0);

  const [updateLeaveRecords, setUpdateLeaveRecords] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState(previousMonth);
  const [year1, setYear1] = useState(today.getFullYear());
  const [selectedYearPass, setSelectedYearPass] = useState(
    previousMonth.split("-")[0]
  );
  const [selectedMonthPass, setSelectedMonthPass] = useState(
    previousMonth.split("-")[1]
  );

  const [lateMark, setLateMark] = useState(0);
  const [completedDays, setCompletedDays] = useState(0);

  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [selectedMode, setSelectedMode] = useState("late"); // "late" | "leave"

  const selectedEmployeeIdRef = useRef(selectedEmployeeId);
  useEffect(() => {
    selectedEmployeeIdRef.current = selectedEmployeeId;
  }, [selectedEmployeeId]);

  /* ------------------------------------------------------------------ */
  /* helpers */
  /* ------------------------------------------------------------------ */
  const fetchEmployees = () =>
    fetch(`${API_URL}/api/employees-list/${year1}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        setEmployees(data);
        if (data.length > 0) {
          setSelectedEmployeeId(data[0].id.toString());
        }
        const emp = data.find(
          (e) => e.id.toString() === selectedEmployeeIdRef.current
        );
        if (emp) populateLeaveFields(emp);
      })
      .catch(() => toast.error("Error fetching employees."));

  const populateLeaveFields = (emp) => {
    setRemainingUnplannedLeave(parseFloat(emp.remainingUnplannedLeave) || 0);
    setExtraLeaves(parseFloat(emp.extra_leaves) || 0);
    setRemainingPlannedLeave(parseFloat(emp.remainingPlannedLeave) || 0);
    setUsedUnplannedLeave(parseFloat(emp.usedUnplannedLeave) || 0);
    setUsedPlannedLeave(parseFloat(emp.usedPlannedLeave) || 0);
    setSelectedEmployeeId(emp.id.toString());
    setEmployee(emp);
  };

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split("-");
    setSelectedMonth(e.target.value);
    setSelectedYearPass(year);
    setSelectedMonthPass(month);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    if (!isNaN(newYear)) {
      setYear1(newYear);
      setSelectedYearPass(newYear.toString());
      setSelectedDates([])
      setUpdateLeaveRecords([])
    }
  };

  useEffect(() => {
    fetchEmployees();
    socket.on("employeeLeavesChanged", fetchEmployees);
    socket.on("employeeLeavesDateChanged", fetchEmployees);
    return () => {
      socket.off("employeeLeavesChanged", fetchEmployees);
      socket.off("employeeLeavesDateChanged", fetchEmployees);
    };
  }, [year1]);

  useEffect(() => {
    const emp = employees.find((x) => x.id.toString() === selectedEmployeeId);
    if (emp) populateLeaveFields(emp);
  }, [selectedEmployeeId, employees]);

  /* ------------------------------------------------------------------ */
  /* leave + late mark fetching — same logic as the calendar             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!selectedEmployeeId || !selectedMonthPass || !selectedYearPass) return;
    if (employees.length === 0) return; // wait for employee list to load

    // Find the employee's name from the employees list
    const emp = employees.find((x) => x.id.toString() === selectedEmployeeId);
    const empName = emp?.name || emp?.Name || emp?.emp_name || "";
    if (!empName) return;

    fetch(
      `${API_URL}/api/attendance/calendar?year=${selectedYearPass}&empName=${encodeURIComponent(empName)}`
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP error ${r.status}`);
        return r.json();
      })
      .then((json) => {
        const attendanceData = Array.isArray(json) ? json : (json?.data || []);
        const monthIndex = parseInt(selectedMonthPass, 10) - 1; // 0-based month
        const yearNum = parseInt(selectedYearPass, 10);

        const lateDates = new Set();

        attendanceData.forEach((record) => {
          const isWfhHalfDay = record.location === "WFH" && parseFloat(record.work_hour || 0) < parseFloat(record.duration || 9);
          if (
            record.isHoliday ||
            record.isCompensatoryLeave ||
            !record.first_CI_time ||
            !record.ci_time ||
            !record.date ||
            record.status === "Half_day" ||
            record.status === "Half Day" ||
            record.status === "Incomplete Attendance" ||
            isWfhHalfDay ||
            record.status === "Site_visit_present" ||
            record.status === "Site_visit_incomplete"
          ) {
            return;
          }

          const recordDate = new Date(record.date);
          if (
            recordDate.getMonth() === monthIndex &&
            recordDate.getFullYear() === yearNum
          ) {
            const isLate = moment(record.first_CI_time, "YYYY-MM-DD h:mmA")
              .isAfter(
                moment(`${record.date} ${record.ci_time}`, "YYYY-MM-DD hh:mm A")
              );

            if (isLate) {
              lateDates.add(record.date);
            }
          }
        });

        const lateCount = lateDates.size;

        setLateMark(lateCount);
        // Same formula as calendar: every 3 late marks = 0.5 day deduction
        setCompletedDays(Math.floor(lateCount / 3) * 0.5);
      })
      .catch((error) => {
        console.error("Error fetching late marks:", error);
        setLateMark(0);
        setCompletedDays(0);
      });
  }, [selectedEmployeeId, selectedMonthPass, selectedYearPass, employees]);

  /* -------------------------------------------------------------- */
  /* update flow */
  /* -------------------------------------------------------------- */
 const settleLeaves = async (
  employeeId,
  lateMark,
  completedDays,
  month,
  year
) => {
  try {
    if (!completedDays || Number(completedDays) <= 0) {
      toast.info("No leave settlement required.");
      return null;
    }

    const response = await fetch(`${API_URL}/api/settle-leaves`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        employee_id: employeeId,
        lateMark,
        completedDays,
        month,
        year,
      }),
    });

    let data;

    try {
      data = await response.json();
    } catch (e) {
      data = await response.text();
    }

    console.log("Status Code:", response.status);
    console.log("Status Text:", response.statusText);
    console.log("Response:", data);

    if (response.status === 409) {
      toast.info(
        data?.message || "Leaves already settled for this month."
      );
      return null;
    }

    if (!response.ok) {
      throw new Error(
        data?.message ||
          data?.error ||
          JSON.stringify(data) ||
          "Failed to settle leaves"
      );
    }

    toast.success(data?.message || "Leaves settled successfully!");
    fetchEmployees();

    return data;
  } catch (err) {
    console.error("Settle Leaves Error:", err);
    console.error("Error Message:", err.message);
    console.error("Stack:", err.stack);

    toast.error(err.message || "Error settling leaves.");

    return null;
  }
};
  const updateEmployeeLeaves = async () => {
    if (!selectedEmployeeId) {
      toast.warn("Select an employee first");
      return;
    }

    if (selectedMode === "late") {
      const result = await settleLeaves(
        selectedEmployeeId,
        lateMark,
        completedDays,
        selectedMonthPass,
        selectedYearPass
      );
      if (!result) return;
    }

    if (selectedMode === "leave") {
      let addPlanned = 0;
      let addUnplan = 0;

      updateLeaveRecords.forEach((r) => {
        const factor = r.leaveDay === "Half" ? 0.5 : 1;
        if (r.leaveType === "Planned") addPlanned += factor;
        if (r.leaveType === "Unplanned") addUnplan += factor;
      });

      let newUsedPlanned = usedPlannedLeave;
      let newUsedUnplanned = usedUnplannedLeave;
      let newRemainingPlanned = remainingPlannedLeave;
      let newRemainingUnplanned = remainingUnplannedLeave;
      let newTotalLeaves = extraLeaves;

      // Planned leave logic
      if (remainingPlannedLeave >= addPlanned) {
        newUsedPlanned += addPlanned;
        newRemainingPlanned -= addPlanned;
      } else {
        const overflowPlanned = addPlanned - remainingPlannedLeave;
        newRemainingPlanned = 0;
        newTotalLeaves += overflowPlanned;
      }

      // Unplanned leave logic
      if (remainingUnplannedLeave >= addUnplan) {
        newUsedUnplanned += addUnplan;
        newRemainingUnplanned -= addUnplan;
      } else {
        const overflowUnplanned = addUnplan - remainingUnplannedLeave;
        newRemainingUnplanned = 0;
        newTotalLeaves += overflowUnplanned;
      }

      try {
        await fetch(
          `${API_URL}/api/employee-leaves/${selectedEmployeeId}/${year1}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              usedUnplannedLeave: newUsedUnplanned,
              usedPlannedLeave: newUsedPlanned,
              remainingUnplannedLeave: newRemainingUnplanned,
              remainingPlannedLeave: newRemainingPlanned,
              extra_leaves: newTotalLeaves,
            }),
          }
        );

        await Promise.all(
          updateLeaveRecords.map((r) =>
            fetch(`${API_URL}/api/employee-leaves-date`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                employeeId: selectedEmployeeId,
                leave_date: r.leaveDate,
                leave_type: r.leaveType,
                leave_day: r.leaveDay,
              }),
            })
          )
        );

        toast.success("Leave records updated successfully");
        setUpdateLeaveRecords([]);
        setSelectedDates([]);
        fetchEmployees();
      } catch (err) {
        toast.error("Error updating leave records");
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* UI handlers */
  /* ------------------------------------------------------------------ */
  const handleDateSelection = (dates) => {
    const formattedDates = dates.map((d) => d.format("YYYY-MM-DD"));
    const filteredRecords = updateLeaveRecords.filter((r) =>
      formattedDates.includes(r.leaveDate)
    );
    const newRecords = formattedDates
      .filter((date) => !filteredRecords.some((r) => r.leaveDate === date))
      .map((date) => ({
        leaveDate: date,
        leaveType: "Planned",
        leaveDay: "Full",
      }));
    setSelectedDates(dates);
    setUpdateLeaveRecords([...filteredRecords, ...newRecords]);
  };

  const handleUpdateLeaveRecordChange = (i, field, val) =>
    setUpdateLeaveRecords((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r))
    );

  const removeUpdateLeaveRecord = (i) =>
    setUpdateLeaveRecords((prev) => prev.filter((_, idx) => idx !== i));

  const handleUpdateConfirmation = () => setShowUpdateConfirmation(true);
  const handleConfirmUpdate = () => {
    setShowUpdateConfirmation(false);
    updateEmployeeLeaves();
  };
  const handleCancelUpdate = () => setShowUpdateConfirmation(false);

  const sortedEmployees = [...employees].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  /* ------------------------------------------------------------------ */
  /* render */
  /* ------------------------------------------------------------------ */
  return (
    <div style={{ height: "auto" }}>
      <Card>
        <Card.Body>
          <Row className="g-4">
            {/* Left Column */}
            <Col lg={6}>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Year</Form.Label>
                    <Form.Control
                      type="number"
                      min="2000"
                      max="9999"
                      value={year1}
                      onChange={handleYearChange}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">
                      Employee
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      className="form-select-sm"
                    >
                      {sortedEmployees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              <div className="border-top pt-3 pb-3">
                <h6>Leave Summary</h6>
                <Row className="g-3">
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Used Unplanned Leave{" "}
                      </h6>{" "}
                      <h4 className="fw-bold">{usedUnplannedLeave}</h4>{" "}
                    </div>{" "}
                  </Col>
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Used Planned Leave{" "}
                      </h6>{" "}
                      <h4 className="fw-bold">{usedPlannedLeave}</h4>{" "}
                    </div>{" "}
                  </Col>
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Remaining Unplanned{" "}
                      </h6>{" "}
                      <h4 className="fw-bold text-success">
                        {" "}
                        {remainingUnplannedLeave}{" "}
                      </h4>{" "}
                    </div>{" "}
                  </Col>
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Remaining Planned{" "}
                      </h6>{" "}
                      <h4 className="fw-bold text-success">
                        {" "}
                        {remainingPlannedLeave}{" "}
                      </h4>{" "}
                    </div>{" "}
                  </Col>{" "}
                </Row>
              </div>
              <div className="border-top pt-3">
                <Row className="g-3">
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Extra Leaves{" "}
                      </h6>{" "}
                      <h4 className="fw-bold text-red">
                        {" "}
                        {employee.extra_leaves || 0}{" "}
                      </h4>{" "}
                    </div>{" "}
                  </Col>
                  <Col md={6}>
                    {" "}
                    <div className="border p-3 rounded bg-light">
                      {" "}
                      <h6 className="text-muted small mb-2">
                        {" "}
                        Extra Late Mark(Days){" "}
                      </h6>{" "}
                      <h4 className="fw-bold text-red">
                        {" "}
                        {employee.total_leaves || 0}{" "}
                      </h4>{" "}
                    </div>{" "}
                  </Col>{" "}
                </Row>
                {/* <p className="text-center fw-semibold small text-muted" style={{ marginTop: "18px" }}> Total Late Mark </p> <div className="bg-light rounded text-center"> <span className="fw-bold" style={{ fontSize: "1.5rem" }}> {employee.total_leaves || 0} </span> </div> */}
              </div>
            </Col>

            {/* Right Column */}
            <Col lg={6}>
              <div className="d-flex flex-column h-100 gap-4">
                <div className="d-flex justify-content-center mb-3">
                  <ButtonGroup>
                    <ToggleButton
                      id="mode-late"
                      type="radio"
                      variant={
                        selectedMode === "late" ? "success" : "outline-success"
                      }
                      name="mode"
                      value="late"
                      checked={selectedMode === "late"}
                      onChange={(e) => setSelectedMode(e.currentTarget.value)}
                    >
                      Late Marks
                    </ToggleButton>
                    <ToggleButton
                      id="mode-leave"
                      type="radio"
                      variant={
                        selectedMode === "leave" ? "success" : "outline-success"
                      }
                      name="mode"
                      value="leave"
                      checked={selectedMode === "leave"}
                      onChange={(e) => setSelectedMode(e.currentTarget.value)}
                    >
                      Leave Records
                    </ToggleButton>
                  </ButtonGroup>
                </div>

                {/* Late Marks */}
                {selectedMode === "late" && (
                  <Card>
                    <Card.Body>
                      <Row className="align-items-end g-3">
                        <Col md={5}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">
                              Month
                            </Form.Label>
                            <Form.Control
                              type="month"
                              value={selectedMonth}
                              min={minMonth}
                              max={maxMonth}
                              onChange={handleMonthChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          {" "}
                          <Form.Group>
                            {" "}
                            <Form.Label className="fw-semibold small">
                              {" "}
                              Late Marks (Days){" "}
                            </Form.Label>
                            <div className="bg-light rounded text-center">
                              <Form.Control
                                type="number"
                                value={lateMark || 0}
                                onChange={(e) =>
                                  setLateMark(Number(e.target.value))
                                }
                                className="fw-bold text-center border-0 bg-transparent"
                                style={{ fontSize: "1.5rem", width: "100%" }}
                              />{" "}
                            </div>{" "}
                          </Form.Group>{" "}
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">
                              Calculated Days
                            </Form.Label>
                            <div
                              className="bg-light rounded text-center py-2"
                              style={{ fontSize: "1.5rem", width: "100%" }}
                            >
                              <span className="fw-bold">
                                {completedDays || "0"}
                              </span>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* Leave Records */}
                {selectedMode === "leave" && (
                  <Card className="flex-grow-1">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5>Leave Date Records</h5>
                        <DatePicker
                          multiple
                          value={selectedDates}
                          onChange={handleDateSelection}
                          format="YYYY-MM-DD"
                          placeholder="Select dates"
                          disableYearPicker={true}
                          minDate={`${year1}-01-01`}
                          maxDate={`${year1}-12-31`}
                          defaultYear={year1}
                          defaultMonth={new Date().getMonth()} // Current month or any month
                        />

                      </div>
                      {updateLeaveRecords.map((r, i) => (
                        <Row
                          key={i}
                          className="align-items-center mb-2 border-bottom pb-2"
                        >
                          <Col md={4}>
                            <Form.Control
                              type="date"
                              value={r.leaveDate}
                              onChange={(e) =>
                                handleUpdateLeaveRecordChange(
                                  i,
                                  "leaveDate",
                                  e.target.value
                                )
                              }
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Select
                              value={r.leaveType}
                              onChange={(e) =>
                                handleUpdateLeaveRecordChange(
                                  i,
                                  "leaveType",
                                  e.target.value
                                )
                              }
                            >
                              <option value="Planned">Planned</option>
                              <option value="Unplanned">Unplanned</option>
                            </Form.Select>
                          </Col>
                          <Col md={3}>
                            <Form.Select
                              value={r.leaveDay}
                              onChange={(e) =>
                                handleUpdateLeaveRecordChange(
                                  i,
                                  "leaveDay",
                                  e.target.value
                                )
                              }
                            >
                              <option value="Full">Full</option>
                              <option value="Half">Half</option>
                            </Form.Select>
                          </Col>
                          <Col md={2}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeUpdateLeaveRecord(i)}
                            >
                              <FaMinus />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </Card.Body>
                  </Card>
                )}

                <div className="mt-auto pt-3">
                  <Button
                    variant="success"
                    onClick={handleUpdateConfirmation}
                    size="lg"
                    className="w-100"
                  >
                    Update Employee Leaves
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showUpdateConfirmation} onHide={handleCancelUpdate}>
        <Modal.Header>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to update leaves?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelUpdate}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmUpdate}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default UpdateLeaves;
